import React, { FC, HTMLAttributes } from 'react'
import { ImageBox, Counter } from '@engyalo/storefront-components'
import { Button, Box, Heading } from '@chakra-ui/core'
import { Price } from '../Price'
import { useRecoilState, useRecoilValue } from 'recoil'
import { cartState, configState } from '../../recoil/atoms'
import { ICartDictionary } from '@engyalo/storefront-types/lib/plugins/model'
import { IProduct } from '@engyalo/storefront-types/lib/plugins/model'
import { useTranslation } from 'react-i18next'
import { generateCartId } from '../../lib/cartIdGenerator'
import { getTotalPrice } from '../../lib/getTotalPrice'

interface IProps extends HTMLAttributes<HTMLDivElement> {
    product: IProduct
}

export const CartItem: FC<IProps> = ({ product }: IProps) => {
    const [cart, setCart] = useRecoilState<ICartDictionary>(cartState)
    const config = useRecoilValue(configState)
    const { t } = useTranslation()

    const getProductFirstImage = (product: IProduct): string => {
        if (product && product.imageURL) return product.imageURL[0] || ''
        return ''
    }

    const addToCart = (product: IProduct, adder = 1): void => {
        const tempCart = { ...cart }
        if (product && product.id) {
            const currentSku = generateCartId(product)
            if (tempCart[currentSku]) {
                const tempProd = { ...tempCart[currentSku] }
                if (tempProd.quantity) {
                    const totalPrice = getTotalPrice(product)
                    tempProd.quantity += adder
                    if (tempProd.quantity > 0) {
                        tempCart[currentSku] = {
                            ...tempProd,
                            totalPrice,
                        }
                        setCart(tempCart)
                    } else {
                        delete tempCart[currentSku]
                        setCart({ ...tempCart })
                    }
                }
            } else if (adder > 0) {
                const totalPrice = getTotalPrice(product)
                tempCart[currentSku] = {
                    ...product,
                    totalPrice,
                    quantity: adder,
                    cartId: currentSku,
                } as IProduct
            }
            setCart(tempCart)
        }
    }

    const deleteItem = (product: IProduct): void => {
        const tempCart = { ...cart }
        if (product && product.cartId) {
            const currentSku = product.cartId
            if (tempCart[currentSku]) {
                delete tempCart[currentSku]
            }
        }
        setCart(tempCart)
    }

    if (product) {
        return (
            <Box borderBottom="1px solid" borderColor="gray.200" display="flex" padding="8px">
                <Box flex="2">
                    <Box display="flex">
                        <Box w="40px">
                            <ImageBox imageUrl={getProductFirstImage(product)} />
                        </Box>
                        <Box paddingLeft="8px">
                            <Heading marginY="4px" as="h3" size="sm" flex="1">
                                {product.name}
                            </Heading>
                            <Box fontSize="xs" fontFamily="body" color="gray.100" paddingBottom="4px">
                                {product.description}
                            </Box>
                            <Price
                                price={product.totalPrice ? product.totalPrice : product.price}
                                currency={config.currencyPrefix}
                            />
                            <Button
                                color="red.500"
                                variant="link"
                                border="transparent"
                                fontSize="sm"
                                textDecor="underline"
                                backgroundColor="transparent"
                                fontWeight="normal"
                                onClick={(): void => deleteItem(product)}
                            >
                                {t('cartItem-delete')}
                            </Button>
                        </Box>
                    </Box>
                </Box>
                <Box flex="1">
                    <Counter
                        name={product.cartId || ''}
                        addAction={(): void => addToCart(product, 1)}
                        minusAction={(): void => addToCart(product, -1)}
                        value={cart[product.cartId || ''] ? cart[product.cartId || ''].quantity : 0}
                    />
                </Box>
            </Box>
        )
    }
    return null
}
